import { FunctionComponent, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { A11y, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FeedCardProfile } from '../../../components';
import { ConfigContext } from '../../../context';
import { StoryCardStatus, StoryCardType } from '../../../services';
import {
  getCssVar,
  IconLabel,
  layoutPath,
  useAppDispatch,
  useAppSelector,
  useSwiperA11y,
} from '../../../shared';
import { getStoryCardsUserProfileFeed, loadMoreStoryCardsUserProfileFeed } from '../../../slices';
import { ProfileFeedCreateCard, ProfileFeedCreateCardMode } from './ProfileFeedCreateCard';

import classes from './ProfileFeed.module.scss';

interface ProfileFeedProps {
  userId: number;
  me?: boolean;
}

export const ProfileFeed: FunctionComponent<ProfileFeedProps> = memo(({ userId, me = true }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    storyCardsUserProfileFeed: cards,
    perPage,
    hasNextPage,
    isFetched,
  } = useAppSelector(({ storyCardsUserProfileFeed }) => storyCardsUserProfileFeed);

  const swiperA11y = useSwiperA11y();

  const { config } = useContext(ConfigContext);

  const { profile, horizontalFeed } = config.elements;

  const { enabled = false } = { ...profile.feed };

  const { cardsPerView = 3, spaceBetweenCards = 12 } = { ...horizontalFeed };

  const slidesPerView = useMemo(() => (isMobileOnly ? 2 : cardsPerView), [cardsPerView]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(
      getStoryCardsUserProfileFeed({
        userId,
        filters: {
          storyCardType: [StoryCardType.IMAGE, StoryCardType.VIDEO],
          ...(!me && { status: [StoryCardStatus.PUBLISHED] }),
        },
      })
    );
  }, [dispatch, enabled, me, userId]);

  const cardsLength = useMemo(() => cards.length, [cards.length]);

  const onSlideChangeHandler = useCallback(
    (activeIndex: number) => {
      const index = activeIndex + slidesPerView;

      if (index >= cardsLength && hasNextPage) {
        dispatch(
          loadMoreStoryCardsUserProfileFeed({
            userId,
            page: Math.floor(index / perPage) + 1,
            size: perPage,
            filters: {
              storyCardType: [StoryCardType.IMAGE, StoryCardType.VIDEO],
              ...(!me && { status: [StoryCardStatus.PUBLISHED] }),
            },
          })
        );
      }
    },
    [cardsLength, dispatch, hasNextPage, me, perPage, slidesPerView, userId]
  );

  const title = useMemo(
    () => <h2 className={classes['profile-feed__title']}>{t('profileFeed.title')}</h2>,
    [t]
  );

  const content = useMemo(() => {
    if (!isFetched) {
      return <Skeleton height={'5rem'} />;
    }

    if (Boolean(me && !cards.length)) {
      return (
        <>
          {title}
          <ProfileFeedCreateCard />
        </>
      );
    }

    if (Boolean(cards.length)) {
      return (
        <div className={classes['profile-feed__horizontal']}>
          <div className={classes['profile-feed__horizontal-header']}>
            {title}

            {me && <ProfileFeedCreateCard mode={ProfileFeedCreateCardMode.LABEL} />}

            <IconLabel
              className={classes['profile-feed__horizontal-header-view']}
              iconId={'right-arrow'}
              iconSize={18}
              label={t('common.view-all')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() => navigate(layoutPath(`/profile${me ? '/' : `/${userId}/`}items`))}
              labelFirst
            />
          </div>

          <Swiper
            navigation
            slidesPerView={slidesPerView}
            spaceBetween={spaceBetweenCards}
            modules={[A11y, Navigation]}
            {...swiperA11y}
            onSlideChange={({ activeIndex }) => onSlideChangeHandler(activeIndex)}
            style={{ overflow: 'visible' }}
            touchStartPreventDefault={false}
            watchSlidesProgress
          >
            {cards.map((card) => (
              <SwiperSlide key={card.id}>
                <FeedCardProfile card={card} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      );
    }

    return null;
  }, [
    cards,
    isFetched,
    me,
    navigate,
    onSlideChangeHandler,
    slidesPerView,
    spaceBetweenCards,
    swiperA11y,
    t,
    title,
    userId,
  ]);

  if (Boolean(!enabled || (!me && isFetched && !cards.length))) {
    return null;
  }

  return <div className={classes['profile-feed']}>{content}</div>;
});
